import {
  StatusBar,
  ModifierBanner,
  Numpad,
  AttemptLog,
  PuzzleGrid,
  HintDeck,
  Toast,
  CRTOverlay,
  useTheme,
} from '@crackpoint/shared/components';
import { strings } from '@crackpoint/shared/config';
import { useKeyboardInput, useRemainingSolutions, useToast } from '@crackpoint/shared/hooks';
import { useGameStore, selectVisibleHints, selectReserveHintsRemaining } from '../../state/gameStore';
import { useSettingsStore } from '../../state';
import type { GameAdapter } from '@crackpoint/shared/adapters';

interface GameScreenProps {
  gameLoop: {
    isLoading: boolean;
    canSubmitGuess: boolean;
    reserveHintsRemaining: number;
    addDigit: (digit: number) => void;
    removeDigit: () => void;
    submitGuess: () => Promise<void>;
    drawReserveHint: () => Promise<void>;
    usePowerUp: (index: number) => Promise<void>;
    returnToTitle: () => void;
  };
  adapter: GameAdapter;
}

export function GameScreen({ gameLoop }: GameScreenProps) {
  const { theme } = useTheme();
  const { message: toastMessage, showToast, dismissToast } = useToast();
  const crtEnabled = useSettingsStore((s) => s.crtEffectsEnabled ?? true);

  const run = useGameStore((s) => s.run);
  const room = useGameStore((s) => s.room);
  const input = useGameStore((s) => s.input);
  const attempts = useGameStore((s) => s.attempts);
  const visibleHints = useGameStore(selectVisibleHints);
  const reserveRemaining = useGameStore(selectReserveHintsRemaining);
  const usedPowerUpIds = useGameStore((s) => s.usedPowerUpIds);

  const { remaining: remainingSolutions, isCalculating } = useRemainingSolutions(
    room?.code_length ?? 3,
    visibleHints,
  );

  useKeyboardInput({
    onDigit: gameLoop.addDigit,
    onDelete: gameLoop.removeDigit,
    onSubmit: () => { gameLoop.submitGuess(); },
    disabled: gameLoop.isLoading,
  });

  const handleDrawReserve = async () => {
    await gameLoop.drawReserveHint();
    showToast(strings.game.reserveToast);
  };

  const handleUsePowerUp = async (index: number) => {
    if (usedPowerUpIds.has(index)) return;
    await gameLoop.usePowerUp(index);
    const powerUp = room?.power_ups[index];
    if (powerUp) {
      showToast(strings.game.powerUpToast(powerUp.type));
    }
  };

  if (!run || !room) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.colours.background.primary,
          color: theme.colours.text.primary,
          fontFamily: theme.typography.fontFamily,
        }}
      >
        {strings.game.loading}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: theme.colours.background.primary,
        color: theme.colours.text.primary,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {crtEnabled && <CRTOverlay />}

      <StatusBar run={run} />

      {room.modifier.id !== 'clean' && (
        <ModifierBanner modifier={room.modifier} />
      )}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing.md,
          gap: theme.spacing.lg,
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: theme.typography.fontSize.sm,
            color: theme.colours.text.secondary,
          }}
        >
          <span>{strings.game.roomLabel(room.room_number)}</span>
          <span>
            {strings.game.remainingLabel(isCalculating ? strings.game.calculating : remainingSolutions)}
          </span>
          <button
            onClick={gameLoop.returnToTitle}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colours.text.tertiary,
              cursor: 'pointer',
              fontSize: theme.typography.fontSize.sm,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            {strings.game.exitButton}
          </button>
        </div>

        <PuzzleGrid
          codeLength={room.code_length}
          currentInput={[...input, ...Array(room.code_length - input.length).fill(null)]}
          selectedSlot={input.length < room.code_length ? input.length : null}
          onSlotSelect={() => {}}
        />

        <Numpad
          onDigit={gameLoop.addDigit}
          onDelete={gameLoop.removeDigit}
          onSubmit={() => { gameLoop.submitGuess(); }}
          canSubmit={gameLoop.canSubmitGuess}
          disabled={gameLoop.isLoading}
        />

        <div
          style={{
            display: 'flex',
            gap: theme.spacing.sm,
            flexWrap: 'wrap',
          }}
        >
          {reserveRemaining > 0 && (
            <button
              onClick={handleDrawReserve}
              disabled={gameLoop.isLoading}
              style={{
                flex: 1,
                padding: theme.spacing.sm,
                backgroundColor: theme.colours.background.elevated,
                border: `2px solid ${theme.colours.background.tertiary}`,
                borderRadius: theme.borders.radius.md,
                color: theme.colours.text.primary,
                cursor: 'pointer',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.sm,
              }}
            >
              {strings.game.reserveButton(reserveRemaining)}
            </button>
          )}

          {room.power_ups.map((powerUp, index) => (
            <button
              key={index}
              onClick={() => handleUsePowerUp(index)}
              disabled={gameLoop.isLoading || usedPowerUpIds.has(index)}
              style={{
                flex: 1,
                padding: theme.spacing.sm,
                backgroundColor: usedPowerUpIds.has(index)
                  ? theme.colours.background.tertiary
                  : theme.colours.background.elevated,
                border: `2px solid ${usedPowerUpIds.has(index) ? theme.colours.background.tertiary : theme.colours.accent.primary}`,
                borderRadius: theme.borders.radius.md,
                color: usedPowerUpIds.has(index) ? theme.colours.text.tertiary : theme.colours.text.primary,
                cursor: usedPowerUpIds.has(index) ? 'not-allowed' : 'pointer',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSize.sm,
                opacity: usedPowerUpIds.has(index) ? 0.5 : 1,
              }}
            >
              {powerUp.type.toUpperCase()}
            </button>
          ))}
        </div>

        <HintDeck
          hints={visibleHints}
          hintsRemaining={reserveRemaining}
          onDrawHint={handleDrawReserve}
          isLoading={gameLoop.isLoading}
        />

        <AttemptLog
          attempts={attempts.map((a) => ({
            digits: a.digits,
            correct_placed: a.correct_placed,
            correct_misplaced: a.correct_misplaced,
            solved: a.solved,
          }))}
        />
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onDismiss={dismissToast} />
      )}
    </div>
  );
}

export default GameScreen;
